import React from 'react';

interface ICheckboxProps {
  id?: string;
  name?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'switch';
  className?: string;
  labelClassName?: string;
  description?: string;
}

const FormCheckbox: React.FC<ICheckboxProps> = ({
  id,
  name,
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
  variant = 'default',
  className = '',
  labelClassName = '',
  description,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Space and Enter to toggle the checkbox
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (!disabled) {
        onChange(!checked);
      }
    }
  };

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const switchSizeClasses = {
    sm: 'w-10 h-5',
    md: 'w-14 h-7',
    lg: 'w-16 h-8',
  };

  const switchThumbClasses = {
    sm: { base: 'h-4 w-4 top-[2px] left-[2px]', translate: 'translate-x-5' },
    md: { base: 'h-6 w-6 top-[2px] left-[2px]', translate: 'translate-x-7' },
    lg: { base: 'h-7 w-7 top-[2px] left-[2px]', translate: 'translate-x-8' },
  };

  if (variant === 'switch') {
    return (
      <div className={`flex items-center ${className}`}>
        <div className='flex items-center'>
          <label className='relative inline-flex items-center cursor-pointer'>
            <input
              type='checkbox'
              id={id}
              name={name}
              checked={checked}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className='sr-only peer'
              tabIndex={disabled ? -1 : 0}
            />
            <div
              className={`
                relative ${switchSizeClasses[size]} rounded-full
                peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-theme-300
                transition-all duration-300 ease-in-out
                ${checked ? 'bg-gradient-to-r from-theme-400 to-theme-500' : 'bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-500 dark:to-gray-600'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              aria-hidden='true'
            >
              <div
                className={`
                  absolute bg-theme-300 dark:bg-theme-400 border-2 border-theme-200 rounded-full
                  transition-all duration-300 ease-in-out
                  ${switchThumbClasses[size].base}
                  ${checked ? switchThumbClasses[size].translate : 'translate-x-0'}
                `}
              />
            </div>
          </label>
        </div>
        {label && (
          <div className='ml-3'>
            <label
              htmlFor={id}
              className={`cursor-pointer ${
                labelClassName ? labelClassName : 'text-base font-semibold text-gray-700'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {label}
            </label>
            {description && <p className='text-sm text-gray-500 mt-2'>{description}</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-start ${className}`}>
      <div className='flex items-center h-5'>
        <input
          id={id}
          name={name}
          type='checkbox'
          checked={checked}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          className={`
                  ${sizeClasses[size]} text-theme-500 border-gray-300 rounded
      focus:ring-theme-500 focus:ring-2 focus:ring-offset-2 transition-colors
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        />
      </div>
      {label && (
        <div className='ml-3'>
          <label
            htmlFor={id}
            className={`cursor-pointer ${
              labelClassName ? labelClassName : 'text-base font-semibold text-gray-700'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {label}
          </label>
          {description && <p className='text-sm text-gray-500 mt-2'>{description}</p>}
        </div>
      )}
    </div>
  );
};

export default FormCheckbox;
